import { paginateSupabase } from '@/lib/supabase-paginate';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format, startOfWeek, startOfMonth, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, endOfWeek, endOfMonth } from 'date-fns';
import type { InternalTransfer } from '@/hooks/finance/useInternalTransfers';

export type ViewPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly';
export type DisplayCurrency = 'ETB' | 'USD';

interface ExchangeRateEntry {
  rate: number;
  effective_date: string;
  rate_type: string;
}

export interface CashFlowRow {
  key: string;
  label: string;
  isHeader?: boolean;
  isTotal?: boolean;
  indent?: number;
  values: Map<string, number>; // periodKey -> amount
}

export function useCashFlowStatement(
  dateFrom: string,
  dateTo: string,
  viewPeriod: ViewPeriod = 'daily',
  displayCurrency: DisplayCurrency = 'ETB'
) {
  const { user } = useAuth();

  // Fetch all exchange rates for the period (for currency conversion)
  const exchangeRatesQuery = useQuery({
    queryKey: ['cash-flow-exchange-rates', dateFrom, dateTo],
    queryFn: async () => {
      const data = await paginateSupabase<any>((from, to) =>
        supabase
          .from('exchange_rates' as any)
          .select('rate, effective_date, rate_type, currency_pair')
          .in('currency_pair', ['USD/ETB', 'EUR/ETB', 'GBP/ETB', 'USD/EUR', 'EUR/USD', 'USD/GBP', 'GBP/USD', 'EUR/GBP', 'GBP/EUR'])
          .lte('effective_date', dateTo)
          .order('effective_date', { ascending: false })
          .range(from, to) as any
      );
      return (data || []) as unknown as (ExchangeRateEntry & { currency_pair: string })[];
    },
    enabled: !!user && !!dateFrom && !!dateTo,
  });

  // Fetch bank account balances
  const balancesQuery = useQuery({
    queryKey: ['cash-flow-balances', dateFrom, dateTo],
    queryFn: async () =>
      await paginateSupabase<any>((from, to) =>
        (supabase as any)
          .from('bank_account_balances')
          .select('*, bank_account:bank_accounts(id, name, currency, exchange_rate_type)')
          .gte('balance_date', dateFrom)
          .lte('balance_date', dateTo)
          .order('balance_date')
          .range(from, to)
      ),
    enabled: !!user,
  });

  // Fetch expenses with category for cash_flow_type classification
  const expensesQuery = useQuery({
    queryKey: ['cash-flow-expenses', dateFrom, dateTo],
    queryFn: async () =>
      await paginateSupabase<any>((from, to) =>
        (supabase as any)
          .from('expenses')
          .select('id, expense_date, amount, total_amount, net_payment, currency, payment_status, category_id, bank_account_id, category:expense_categories(id, name, cash_flow_type, category_type, parent_id)')
          .gte('expense_date', dateFrom)
          .lte('expense_date', dateTo)
          .eq('payment_status', 'paid')
          .order('expense_date', { ascending: true })
          .range(from, to)
      ),
    enabled: !!user,
  });


  // Fetch all expense categories for hierarchy traversal
  const categoriesQuery = useQuery({
    queryKey: ['cash-flow-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('id, name, cash_flow_type, category_type, parent_id');
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch ChipChip sales (aggregated by date at DB level)
  const salesQuery = useQuery({
    queryKey: ['cash-flow-sales', dateFrom, dateTo],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_daily_sales_totals', {
        p_start_date: dateFrom,
        p_end_date: dateTo,
      });
      if (error) throw error;
      return (data || []) as { sale_date: string; total_revenue: number }[];
    },
    enabled: !!user,
  });

  // Fetch B2B sales data
  const b2bQuery = useQuery({
    queryKey: ['cash-flow-b2b-sales', dateFrom, dateTo],
    queryFn: async () =>
      await paginateSupabase<any>((from, to) =>
        (supabase as any)
          .from('b2b_sales_data')
          .select('id, sale_date, quantity_sold, selling_price, total_revenue, product_name')
          .gte('sale_date', dateFrom)
          .lte('sale_date', dateTo)
          .order('sale_date', { ascending: true })
          .range(from, to)
      ),
    enabled: !!user,
  });

  // Fetch bank accounts for currency info
  const bankAccountsQuery = useQuery({
    queryKey: ['cash-flow-bank-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('id, name, currency, exchange_rate_type')
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch internal transfers
  const transfersQuery = useQuery({
    queryKey: ['cash-flow-internal-transfers', dateFrom, dateTo],
    queryFn: async () =>
      (await paginateSupabase<any>((from, to) =>
        (supabase as any)
          .from('internal_transfers')
          .select('*')
          .gte('transfer_date', dateFrom)
          .lte('transfer_date', dateTo)
          .order('transfer_date', { ascending: true })
          .range(from, to)
      )) as unknown as InternalTransfer[],
    enabled: !!user && !!dateFrom && !!dateTo,
  });

  // Fetch cash inflows for financing activities
  const cashInflowsQuery = useQuery({
    queryKey: ['cash-flow-cash-inflows', dateFrom, dateTo],
    queryFn: async () =>
      (await paginateSupabase<any>((from, to) =>
        (supabase as any)
          .from('cash_inflows')
          .select('id, inflow_date, amount, currency, bank_account_id, cash_flow_type, category')
          .gte('inflow_date', dateFrom)
          .lte('inflow_date', dateTo)
          .order('inflow_date', { ascending: true })
          .range(from, to)
      )) as unknown as { id: string; inflow_date: string; amount: number; currency: string; bank_account_id: string | null; cash_flow_type: string; category: string }[],
    enabled: !!user && !!dateFrom && !!dateTo,
  });

  // Build rate lookup: find most recent rate on or before a given date
  const getRateForDate = useMemo(() => {
    const rates = exchangeRatesQuery.data || [];
    // Rates are already sorted desc by effective_date
    return (date: string, rateType: string, currencyPair: string = 'USD/ETB'): number => {
      const found = rates.find(
        r => r.rate_type === rateType && r.currency_pair === currencyPair && r.effective_date <= date
      );
      return found ? Number(found.rate) : 1;
    };
  }, [exchangeRatesQuery.data]);

  // Convert amount to display currency
  const convertAmount = useMemo(() => {
    const PAIR_MAP: Record<string, string> = {
      USD: 'USD/ETB',
      EUR: 'EUR/ETB',
      GBP: 'GBP/ETB',
    };

    return (amount: number, sourceCurrency: string, date: string, rateType: string = 'bank_rate'): number => {
      if (sourceCurrency === displayCurrency) return amount;

      // Try direct pair first (e.g. USD/EUR or EUR/USD)
      const directPair = `${sourceCurrency}/${displayCurrency}`;
      const directRate = getRateForDate(date, rateType, directPair);
      if (directRate > 0 && directRate !== 1) {
        return amount * directRate;
      }
      // Try inverse direct pair
      const inversePair = `${displayCurrency}/${sourceCurrency}`;
      const inverseRate = getRateForDate(date, rateType, inversePair);
      if (inverseRate > 0 && inverseRate !== 1) {
        return amount / inverseRate;
      }

      // Converting foreign currency to ETB
      if (displayCurrency === 'ETB' && PAIR_MAP[sourceCurrency]) {
        const rate = getRateForDate(date, rateType, PAIR_MAP[sourceCurrency]);
        return rate > 0 ? amount * rate : amount;
      }

      // Converting ETB to foreign currency
      if (sourceCurrency === 'ETB' && PAIR_MAP[displayCurrency]) {
        const rate = getRateForDate(date, rateType, PAIR_MAP[displayCurrency]);
        return rate > 0 ? amount / rate : amount;
      }

      // Cross-currency (e.g. EUR -> USD): go through ETB
      if (PAIR_MAP[sourceCurrency] && PAIR_MAP[displayCurrency]) {
        const toEtb = getRateForDate(date, rateType, PAIR_MAP[sourceCurrency]);
        const fromEtb = getRateForDate(date, rateType, PAIR_MAP[displayCurrency]);
        if (toEtb > 0 && fromEtb > 0) return (amount * toEtb) / fromEtb;
      }

      return amount;
    };
  }, [displayCurrency, getRateForDate]);

  // Generate period keys
  const periodKeys = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    const from = new Date(dateFrom);
    const to = new Date(dateTo);

    if (viewPeriod === 'daily') {
      return eachDayOfInterval({ start: from, end: to }).map(d => format(d, 'yyyy-MM-dd'));
    }
    if (viewPeriod === 'weekly') {
      return eachWeekOfInterval({ start: from, end: to }, { weekStartsOn: 1 }).map(d => format(d, 'yyyy-MM-dd'));
    }
    return eachMonthOfInterval({ start: from, end: to }).map(d => format(d, 'yyyy-MM'));
  }, [dateFrom, dateTo, viewPeriod]);

  // Map a date to its period key
  const dateToPeriodKey = useMemo(() => {
    return (dateStr: string): string => {
      const d = new Date(dateStr);
      if (viewPeriod === 'daily') return format(d, 'yyyy-MM-dd');
      if (viewPeriod === 'weekly') return format(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      return format(d, 'yyyy-MM');
    };
  }, [viewPeriod]);

  // Format period key for display
  const formatPeriodKey = useMemo(() => {
    return (key: string): string => {
      if (viewPeriod === 'daily') return format(new Date(key), 'MMM d');
      if (viewPeriod === 'weekly') return `W ${format(new Date(key), 'MMM d')}`;
      return format(new Date(key + '-01'), 'MMM yyyy');
    };
  }, [viewPeriod]);

  // Build the cash flow grid
  const { rows, periodLabels } = useMemo(() => {
    const expenses = expensesQuery.data || [];
    const sales = salesQuery.data || [];
    const b2b = b2bQuery.data || [];
    const balances = balancesQuery.data || [];
    const categories = categoriesQuery.data || [];
    const bankAccounts = bankAccountsQuery.data || [];
    const transfers = transfersQuery.data || [];
    const cashInflows = cashInflowsQuery.data || [];

    const categoryMap = new Map(categories.map(c => [c.id, c]));
    const bankAccountMap = new Map(bankAccounts.map(a => [a.id, a]));

    // Helper: resolve cash_flow_type by walking up category hierarchy
    const getCashFlowType = (categoryId: string | null): string => {
      if (!categoryId) return 'operating';
      let current = categoryMap.get(categoryId);
      while (current) {
        if (current.cash_flow_type) return current.cash_flow_type;
        if (current.parent_id) current = categoryMap.get(current.parent_id);
        else break;
      }
      return 'operating';
    };

    // Helper: get expense label from category hierarchy (cost_group level)
    const getExpenseLabel = (categoryId: string | null): string => {
      if (!categoryId) return 'Other Expenses';
      let current = categoryMap.get(categoryId);
      // Walk up to find cost_group level
      while (current) {
        if (current.category_type === 'cost_group') return current.name;
        if (current.parent_id) {
          const parent = categoryMap.get(current.parent_id);
          if (parent && parent.category_type === 'cost_group') return parent.name;
          current = parent || null;
        } else break;
      }
      // Fallback to category name
      const orig = categoryMap.get(categoryId);
      return orig?.name || 'Other Expenses';
    };

    // Initialize row maps
    const createRow = (): Map<string, number> => new Map(periodKeys.map(k => [k, 0]));

    // Sales inflows
    const chipchipSalesRow = createRow();
    const b2bSalesRow = createRow();

    for (const sale of sales) {
      const pk = dateToPeriodKey(sale.sale_date);
      const revenue = Number(sale.total_revenue) || 0;
      const converted = convertAmount(revenue, 'ETB', sale.sale_date, 'bank_rate');
      chipchipSalesRow.set(pk, (chipchipSalesRow.get(pk) || 0) + converted);
    }

    for (const sale of b2b) {
      const pk = dateToPeriodKey(sale.sale_date);
      const revenue = Number(sale.total_revenue) || 0;
      const converted = convertAmount(revenue, 'ETB', sale.sale_date, 'bank_rate');
      b2bSalesRow.set(pk, (b2bSalesRow.get(pk) || 0) + converted);
    }

    // Internal transfers FX effect
    const transfersFxRow = createRow();
    for (const t of transfers) {
      const pk = dateToPeriodKey(t.transfer_date);
      if (t.from_currency !== t.to_currency) {
        const fromConverted = convertAmount(t.from_amount, t.from_currency, t.transfer_date, 'bank_rate');
        const toConverted = convertAmount(t.to_amount, t.to_currency, t.transfer_date, 'bank_rate');
        const fxEffect = toConverted - fromConverted;
        transfersFxRow.set(pk, (transfersFxRow.get(pk) || 0) + fxEffect);
      }
    }

    // Expense outflows grouped by cash_flow_type and cost_group
    const operatingExpenseGroups = new Map<string, Map<string, number>>();
    const investingExpenseGroups = new Map<string, Map<string, number>>();
    const financingExpenseGroups = new Map<string, Map<string, number>>();

    for (const exp of expenses) {
      const cashFlowType = getCashFlowType(exp.category_id);
      const label = getExpenseLabel(exp.category_id);
      const pk = dateToPeriodKey(exp.expense_date);
      const amount = exp.net_payment ?? exp.total_amount ?? exp.amount ?? 0;
      const currency = exp.currency || 'ETB';

      // Determine rate type from bank account
      const bankAccount = exp.bank_account_id ? bankAccountMap.get(exp.bank_account_id) : null;
      const rateType = (bankAccount as any)?.exchange_rate_type || 'bank_rate';

      const converted = convertAmount(amount, currency, exp.expense_date, rateType);

      let targetGroup: Map<string, Map<string, number>>;
      if (cashFlowType === 'investing') targetGroup = investingExpenseGroups;
      else if (cashFlowType === 'financing') targetGroup = financingExpenseGroups;
      else targetGroup = operatingExpenseGroups;

      if (!targetGroup.has(label)) targetGroup.set(label, createRow());
      const row = targetGroup.get(label)!;
      row.set(pk, (row.get(pk) || 0) + converted);
    }

    // Opening/Closing Balances from bank_account_balances
    const openingBalanceRow = createRow();
    const closingBalanceRow = createRow();

    // Group balances by period and sum across accounts
    for (const bal of balances) {
      const pk = dateToPeriodKey(bal.balance_date);
      const bankAccount = bal.bank_account as any;
      const currency = bankAccount?.currency || bal.currency || 'ETB';
      const rateType = bankAccount?.exchange_rate_type || 'bank_rate';

      if (bal.opening_balance != null) {
        const conv = convertAmount(bal.opening_balance, currency, bal.balance_date, rateType);
        openingBalanceRow.set(pk, (openingBalanceRow.get(pk) || 0) + conv);
      }
      if (bal.closing_balance != null) {
        const conv = convertAmount(bal.closing_balance, currency, bal.balance_date, rateType);
        closingBalanceRow.set(pk, (closingBalanceRow.get(pk) || 0) + conv);
      }
    }

    // Calculate totals
    const totalSalesRow = createRow();
    const totalOperatingRow = createRow();
    const totalInvestingRow = createRow();
    const totalFinancingRow = createRow();
    const netCashFlowRow = createRow();

    // Cash inflows (financing inflows)
    const INFLOW_CATEGORY_LABELS: Record<string, string> = {
      capital_injection: 'Capital Injection',
      loan_proceeds: 'Loan Proceeds',
      asset_sale: 'Asset Sale',
      grant: 'Grants Received',
      refund: 'Refund',
      other: 'Other Inflows',
    };
    const financingInflowGroups = new Map<string, Map<string, number>>();

    for (const inflow of cashInflows) {
      const pk = dateToPeriodKey(inflow.inflow_date);
      const label = INFLOW_CATEGORY_LABELS[inflow.category] || 'Other Inflows';
      const currency = inflow.currency || 'ETB';
      const bankAccount = inflow.bank_account_id ? bankAccountMap.get(inflow.bank_account_id) : null;
      const rateType = (bankAccount as any)?.exchange_rate_type || 'bank_rate';
      const converted = convertAmount(inflow.amount, currency, inflow.inflow_date, rateType);

      if (!financingInflowGroups.has(label)) financingInflowGroups.set(label, createRow());
      const row = financingInflowGroups.get(label)!;
      row.set(pk, (row.get(pk) || 0) + converted);
    }

    for (const pk of periodKeys) {
      const salesTotal = (chipchipSalesRow.get(pk) || 0) + (b2bSalesRow.get(pk) || 0);
      totalSalesRow.set(pk, salesTotal);

      let opTotal = 0;
      operatingExpenseGroups.forEach(row => { opTotal += row.get(pk) || 0; });
      totalOperatingRow.set(pk, salesTotal - opTotal);

      let invTotal = 0;
      investingExpenseGroups.forEach(row => { invTotal += row.get(pk) || 0; });
      totalInvestingRow.set(pk, -invTotal);

      let finExpTotal = 0;
      financingExpenseGroups.forEach(row => { finExpTotal += row.get(pk) || 0; });
      let finInflowTotal = 0;
      financingInflowGroups.forEach(row => { finInflowTotal += row.get(pk) || 0; });
      const fxEffect = transfersFxRow.get(pk) || 0;
      totalFinancingRow.set(pk, finInflowTotal - finExpTotal + fxEffect);

      netCashFlowRow.set(pk, (salesTotal - opTotal) + (-invTotal) + (finInflowTotal - finExpTotal + fxEffect));
    }

    // Build rows array
    const allRows: CashFlowRow[] = [];

    // Opening Balance
    allRows.push({ key: 'opening', label: 'Opening Balance', isTotal: true, values: openingBalanceRow });

    // Operating Activities
    allRows.push({ key: 'op-header', label: 'OPERATING ACTIVITIES', isHeader: true, values: createRow() });
    allRows.push({ key: 'chipchip-sales', label: 'ChipChip Sales', indent: 1, values: chipchipSalesRow });
    allRows.push({ key: 'b2b-sales', label: 'B2B Sales', indent: 1, values: b2bSalesRow });
    allRows.push({ key: 'total-sales', label: 'Total Sales Inflow', isTotal: true, indent: 1, values: totalSalesRow });

    // Operating expense sub-rows
    const sortedOpGroups = [...operatingExpenseGroups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [label, row] of sortedOpGroups) {
      allRows.push({ key: `op-exp-${label}`, label: `(${label})`, indent: 1, values: row });
    }
    allRows.push({ key: 'net-operating', label: 'Net Operating Cash Flow', isTotal: true, values: totalOperatingRow });

    // Investing Activities
    allRows.push({ key: 'inv-header', label: 'INVESTING ACTIVITIES', isHeader: true, values: createRow() });
    const sortedInvGroups = [...investingExpenseGroups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [label, row] of sortedInvGroups) {
      allRows.push({ key: `inv-exp-${label}`, label: `(${label})`, indent: 1, values: row });
    }
    allRows.push({ key: 'net-investing', label: 'Net Investing Cash Flow', isTotal: true, values: totalInvestingRow });

    // Financing Activities
    allRows.push({ key: 'fin-header', label: 'FINANCING ACTIVITIES', isHeader: true, values: createRow() });
    // Financing inflows (positive)
    const sortedFinInflowGroups = [...financingInflowGroups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [label, row] of sortedFinInflowGroups) {
      allRows.push({ key: `fin-in-${label}`, label, indent: 1, values: row });
    }
    // Financing outflows (negative)
    const sortedFinGroups = [...financingExpenseGroups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    for (const [label, row] of sortedFinGroups) {
      allRows.push({ key: `fin-exp-${label}`, label: `(${label})`, indent: 1, values: row });
    }
    // Add FX effect from internal transfers if any non-zero values exist
    const hasFxEffect = [...transfersFxRow.values()].some(v => v !== 0);
    if (hasFxEffect) {
      allRows.push({ key: 'fin-fx-transfers', label: 'Internal Transfers (Net FX Effect)', indent: 1, values: transfersFxRow });
    }
    allRows.push({ key: 'net-financing', label: 'Net Financing Cash Flow', isTotal: true, values: totalFinancingRow });

    // Net Cash Flow & Closing Balance
    allRows.push({ key: 'net-cash-flow', label: 'Net Cash Flow', isTotal: true, values: netCashFlowRow });
    allRows.push({ key: 'closing', label: 'Closing Balance', isTotal: true, values: closingBalanceRow });

    const labels = periodKeys.map(k => ({ key: k, label: formatPeriodKey(k) }));

    return { rows: allRows, periodLabels: labels };
  }, [
    expensesQuery.data, salesQuery.data, b2bQuery.data, balancesQuery.data,
    categoriesQuery.data, bankAccountsQuery.data, transfersQuery.data, cashInflowsQuery.data,
    periodKeys, dateToPeriodKey, convertAmount, formatPeriodKey,
  ]);

  const isLoading = exchangeRatesQuery.isLoading || balancesQuery.isLoading ||
    expensesQuery.isLoading || categoriesQuery.isLoading || salesQuery.isLoading ||
    b2bQuery.isLoading || bankAccountsQuery.isLoading || transfersQuery.isLoading ||
    cashInflowsQuery.isLoading;

  return {
    rows,
    periodLabels,
    periodKeys,
    isLoading,
    displayCurrency,
  };
}
